import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import type { Task } from '../page';

interface ClientFinancialProps {
  tasks: Task[];
  projectId: string;
}

interface SprintEntry {
  id: string;
  description: string | null;
  amount: number;
  is_paid: boolean;
  entry_date: string;
  due_date: string | null;
  task_id: string | null; // sprint id stored here
}

const BOARD_STATUS_LABELS: Record<string, { label: string; color: string }> = {
  backlog: { label: 'Backlog', color: 'bg-slate-100 text-slate-600' },
  todo: { label: 'A Fazer', color: 'bg-slate-100 text-slate-600' },
  doing: { label: 'Fazendo', color: 'bg-amber-100 text-amber-700' },
  validation: { label: 'Em Validação', color: 'bg-violet-100 text-violet-700' },
  done: { label: 'Concluído', color: 'bg-emerald-100 text-emerald-700' },
};

export default function ClientFinancial({ tasks, projectId }: ClientFinancialProps) {
  const [sprintEntries, setSprintEntries] = useState<SprintEntry[]>([]);
  const [loadingEntries, setLoadingEntries] = useState(true);

  useEffect(() => {
    const fetchSprintEntries = async () => {
      setLoadingEntries(true);
      const { data } = await supabase
        .from('financial_entries')
        .select('id, description, amount, is_paid, entry_date, due_date, task_id')
        .eq('project_id', projectId)
        .eq('origin', 'sprint')
        .eq('is_active', true)
        .order('entry_date', { ascending: false });
      setSprintEntries(data || []);
      setLoadingEntries(false);
    };
    fetchSprintEntries();
  }, [projectId]);

  const fmt = (v: number) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  const fmtDate = (d: string) => new Date(d + 'T12:00:00').toLocaleDateString('pt-BR');

  const pricedTasks = tasks.filter(t => t.value > 0);
  const totalTaskValue = pricedTasks.reduce((s, t) => s + Number(t.value), 0);
  const totalTaskPaid = pricedTasks.filter(t => t.paid).reduce((s, t) => s + Number(t.value), 0);

  const totalSprintValue = sprintEntries.reduce((s, e) => s + Number(e.amount), 0);
  const totalSprintPaid = sprintEntries.filter(e => e.is_paid).reduce((s, e) => s + Number(e.amount), 0);

  const totalValue = totalTaskValue + totalSprintValue;
  const totalPaid = totalTaskPaid + totalSprintPaid;
  const totalPending = totalValue - totalPaid;

  const hasSprints = sprintEntries.length > 0;
  const hasTasks = pricedTasks.length > 0;
  const hasAny = hasSprints || hasTasks;

  return (
    <div>
      {/* Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <p className="text-slate-400 text-xs font-medium uppercase tracking-wide">Valor Total</p>
          <p className="text-2xl font-bold text-slate-800 mt-1">{fmt(totalValue)}</p>
          <p className="text-slate-400 text-xs mt-1">
            {hasSprints && `${sprintEntries.length} sprint${sprintEntries.length !== 1 ? 's' : ''}`}
            {hasSprints && hasTasks && ' · '}
            {hasTasks && `${pricedTasks.length} atividade${pricedTasks.length !== 1 ? 's' : ''}`}
            {!hasAny && 'Nenhum lançamento'}
          </p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <p className="text-slate-400 text-xs font-medium uppercase tracking-wide">Pago</p>
          <p className="text-2xl font-bold text-emerald-600 mt-1">{fmt(totalPaid)}</p>
          <p className="text-slate-400 text-xs mt-1">
            {sprintEntries.filter(e => e.is_paid).length + pricedTasks.filter(t => t.paid).length} lançamentos pagos
          </p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <p className="text-slate-400 text-xs font-medium uppercase tracking-wide">Pendente</p>
          <p className="text-2xl font-bold text-amber-600 mt-1">{fmt(totalPending)}</p>
          <p className="text-slate-400 text-xs mt-1">
            {sprintEntries.filter(e => !e.is_paid).length + pricedTasks.filter(t => !t.paid).length} lançamentos pendentes
          </p>
        </div>
      </div>

      {/* Barra de progresso financeiro */}
      {totalValue > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 p-5 mb-6">
          <div className="flex items-center justify-between mb-2">
            <p className="text-slate-600 text-sm font-medium">Progresso de pagamento</p>
            <p className="text-slate-500 text-sm">{Math.round((totalPaid / totalValue) * 100)}%</p>
          </div>
          <div className="h-3 bg-slate-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-emerald-400 rounded-full transition-all"
              style={{ width: `${Math.min((totalPaid / totalValue) * 100, 100)}%` }}
            ></div>
          </div>
          <div className="flex items-center justify-between mt-2 text-xs text-slate-400">
            <span>{fmt(totalPaid)} pago</span>
            <span>{fmt(totalPending)} pendente</span>
          </div>
        </div>
      )}

      {/* Sprints */}
      {!loadingEntries && hasSprints && (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden mb-5">
          <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-2">
            <i className="ri-time-line text-cyan-500 text-sm"></i>
            <h3 className="text-slate-700 font-semibold text-sm">Sprints</h3>
            <span className="ml-auto px-2 py-0.5 rounded-full text-xs bg-slate-100 text-slate-500">{sprintEntries.length}</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-100 bg-slate-50">
                  <th className="text-left px-5 py-3 text-slate-500 font-medium">Sprint</th>
                  <th className="text-left px-5 py-3 text-slate-500 font-medium">Data</th>
                  <th className="text-left px-5 py-3 text-slate-500 font-medium">Vencimento</th>
                  <th className="text-right px-5 py-3 text-slate-500 font-medium">Valor</th>
                  <th className="text-center px-5 py-3 text-slate-500 font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {sprintEntries.map(entry => (
                  <tr key={entry.id} className="border-b border-slate-50 hover:bg-slate-50 transition-colors">
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-cyan-50">
                          <i className="ri-time-line text-cyan-500 text-xs"></i>
                        </div>
                        <p className="text-slate-700 font-medium text-sm">{entry.description || 'Sprint'}</p>
                      </div>
                    </td>
                    <td className="px-5 py-3 text-slate-500 text-xs">{fmtDate(entry.entry_date)}</td>
                    <td className="px-5 py-3 text-slate-500 text-xs">
                      {entry.due_date ? fmtDate(entry.due_date) : '—'}
                    </td>
                    <td className="px-5 py-3 text-right">
                      <span className="text-slate-700 font-semibold">{fmt(Number(entry.amount))}</span>
                    </td>
                    <td className="px-5 py-3 text-center">
                      {entry.is_paid ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700">
                          <i className="ri-checkbox-circle-fill"></i>Pago
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-50 text-amber-700">
                          <i className="ri-time-line"></i>Pendente
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Atividades precificadas */}
      {!hasAny && !loadingEntries ? (
        <div className="flex flex-col items-center justify-center py-16 text-slate-400">
          <i className="ri-money-dollar-circle-line text-4xl mb-3"></i>
          <p className="text-sm font-medium text-slate-500">Nenhum lançamento financeiro</p>
          <p className="text-xs mt-1">Os valores serão exibidos quando o administrador definir sprints ou preços</p>
        </div>
      ) : hasTasks ? (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-2">
            <i className="ri-list-check-2 text-violet-500 text-sm"></i>
            <h3 className="text-slate-700 font-semibold text-sm">Atividades e Valores</h3>
            <span className="ml-auto px-2 py-0.5 rounded-full text-xs bg-slate-100 text-slate-500">{pricedTasks.length}</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-100 bg-slate-50">
                  <th className="text-left px-5 py-3 text-slate-500 font-medium">Atividade</th>
                  <th className="text-left px-5 py-3 text-slate-500 font-medium">Tipo</th>
                  <th className="text-left px-5 py-3 text-slate-500 font-medium">Status</th>
                  <th className="text-left px-5 py-3 text-slate-500 font-medium">Estimativa</th>
                  <th className="text-right px-5 py-3 text-slate-500 font-medium">Valor</th>
                  <th className="text-center px-5 py-3 text-slate-500 font-medium">Pagamento</th>
                </tr>
              </thead>
              <tbody>
                {pricedTasks.map(task => {
                  const st = BOARD_STATUS_LABELS[task.board_status] || { label: task.board_status, color: 'bg-slate-100 text-slate-600' };
                  return (
                    <tr key={task.id} className="border-b border-slate-50 hover:bg-slate-50 transition-colors">
                      <td className="px-5 py-3 max-w-xs">
                        <p className="text-slate-700 font-medium truncate">{task.title}</p>
                        {task.description && (
                          <p className="text-slate-400 text-xs truncate mt-0.5">{task.description}</p>
                        )}
                      </td>
                      <td className="px-5 py-3">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                          task.type === 'bug' ? 'bg-red-50 text-red-600' : 'bg-violet-50 text-violet-600'
                        }`}>
                          <i className={task.type === 'bug' ? 'ri-bug-line' : 'ri-add-circle-line'}></i>
                          {task.type === 'bug' ? 'Bug' : 'Demanda'}
                        </span>
                      </td>
                      <td className="px-5 py-3">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${st.color}`}>{st.label}</span>
                      </td>
                      <td className="px-5 py-3 text-slate-500 text-xs">
                        {task.estimate_hours > 0 ? `${task.estimate_hours}h` : ''}
                        {task.estimate_hours > 0 && task.estimate_days > 0 ? ' / ' : ''}
                        {task.estimate_days > 0 ? `${task.estimate_days}d` : ''}
                        {!task.estimate_hours && !task.estimate_days ? '—' : ''}
                      </td>
                      <td className="px-5 py-3 text-right">
                        <span className="text-slate-700 font-semibold">{fmt(Number(task.value))}</span>
                      </td>
                      <td className="px-5 py-3 text-center">
                        {task.paid ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700">
                            <i className="ri-checkbox-circle-fill"></i>Pago
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-50 text-amber-700">
                            <i className="ri-time-line"></i>Pendente
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}
    </div>
  );
}
